'use es6';

import { Component } from 'react';
import compose from 'transmute/compose';
import merge from 'transmute/merge';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { DragSource, DropTarget } from 'react-dnd';
import * as DragDirections from './DragDirections';
import { jsx as _jsx } from "react/jsx-runtime";
const ITEM_TARGET = {
  drop(props, monitor) {
    if (props.onDrop != null) {
      props.onDrop(monitor.getItem(), monitor);
    }
  },
  hover(props, monitor, component) {
    let clientOffset;
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;
    const {
      listKey
    } = props;
    const {
      dragDirection
    } = props;

    // Dragging item from a different container into this one
    // Change the list key to avoid infinite loops
    if (dragIndex === -1 || monitor.getItem().listKey !== listKey) {
      props.onAddOption(monitor.getItem(), hoverIndex, listKey);
      if (typeof props.onRemoveOption === 'function') {
        props.onRemoveOption(monitor.getItem());
      }
      monitor.getItem().index = hoverIndex;
      monitor.getItem().listKey = listKey;
      return;
    }

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();
    if (dragDirection === DragDirections.HORIZONTAL) {
      // Get horizontal middle
      const hoverCrossoverPoint = hoverBoundingRect.right - hoverBoundingRect.left;

      // Determine mouse position
      clientOffset = monitor.getClientOffset();

      // Get pixels to the left
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      // When dragging left, only move when the cursor is greater than 90%
      // When dragging right, only move when the cursor is less than 10%

      // Dragging right
      const draggingRight = dragIndex < hoverIndex && hoverClientX < hoverCrossoverPoint * 0.1;

      // Dragging left
      const draggingLeft = dragIndex > hoverIndex && hoverClientX > hoverCrossoverPoint * 0.9;
      if (draggingLeft || draggingRight) {
        return;
      }
    } else {
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      const draggingDown = dragIndex < hoverIndex && hoverClientY < hoverMiddleY;

      // Dragging upwards
      const draggingUp = dragIndex > hoverIndex && hoverClientY > hoverMiddleY;
      if (draggingDown || draggingUp) {
        return;
      }
    }

    // Time to actually perform the action
    props.onMove(dragIndex, hoverIndex, monitor.getItem());

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex;
  }
};
const defaultData = () => ({});
export default function (itemType, data = defaultData, override = {}) {
  let itemTypeSource;
  let itemTypeTarget;
  if (typeof itemType === 'object') {
    ({
      itemTypeSource,
      itemTypeTarget
    } = itemType);
  } else {
    itemTypeTarget = itemType;
    itemTypeSource = itemType;
  }
  const ITEM_SOURCE = merge(override, {
    beginDrag(props) {
      return {
        index: props.index,
        originalIndex: props.index,
        listKey: props.listKey,
        data: data(props),
        useCustomDragLayer: props.useCustomDragLayer
      };
    },
    endDrag(props, monitor) {
      const {
        originalIndex,
        index
      } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        props.onMove(index, originalIndex, monitor.getItem());
        if (props.onInvalidDrop != null) {
          props.onInvalidDrop(index, originalIndex, monitor.getItem());
        }
      }
    }
  });
  return WrappedComponent => {
    const componentName = WrappedComponent.displayName || WrappedComponent.name;
    // Must be a class component instead of a functional component so that it
    // can be located using `findDOMNode` above. Functional components can not
    // be located via `findDOMNode`.
    // See https://reactjs.org/docs/strict-mode.html#warning-about-deprecated-finddomnode-usage
    // See https://github.com/reactjs/rfcs/pull/97
    class DnDListWrapper extends Component {
      render() {
        return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props));
      }
    }
    DnDListWrapper.displayName = `DnDListWrapper(${componentName})`;
    DnDListWrapper.propTypes = {
      index: PropTypes.number.isRequired,
      dragDirection: PropTypes.oneOf(Object.keys(DragDirections))
    };
    DnDListWrapper.defaultProps = {
      dragDirection: DragDirections.VERTICAL
    };
    return compose(DragSource(itemTypeSource, ITEM_SOURCE, (connect, monitor) => ({
      connectDragSource: connect.dragSource(),
      isDragging: monitor.isDragging(),
      connectDragPreview: connect.dragPreview(),
      itemTypeSource: monitor.internalMonitor.store.getState().dragOperation.itemType
    })), DropTarget(itemTypeTarget, ITEM_TARGET, (connect, monitor) => ({
      connectDropTarget: connect.dropTarget(),
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({
        shallow: true
      })
    })))(DnDListWrapper);
  };
}